import React from 'react';
import { BrandLogo } from './BrandLogo';
import { personAsset } from '../content/namedPeople';

// Promotional/illustrative cut-outs only — never member photos. Dimensions fixed via aspect-ratio to avoid layout shift.
const PHOTOS = {
  'hero-man-black': { w: 302, h: 663, widths: [302] },
  'hero-girl-redbag': { w: 316, h: 483, widths: [316] },
  'hero-girl-lace': { w: 225, h: 483, widths: [225] },
  'hero-man-denim': { w: 355, h: 627, widths: [355] },
  girl104: { w: 720, h: 1291, widths: [480, 720] },
  men21: { w: 509, h: 627, widths: [480, 509] },
  girl106: { w: 348, h: 641, widths: [348] },
  group8: { w: 1200, h: 1475, widths: [480, 900] },
  girl97: { w: 736, h: 1308, widths: [480, 736] },
  kogal2: { w: 337, h: 588, widths: [337] },
  girl103: { w: 324, h: 479, widths: [324] },
  girl96: { w: 340, h: 524, widths: [340] },
};

export const PromoPhoto = ({ id, className = '', sizes = '320px', alt = 'FlameLink promotional model photo', priority = false, testId, fit = 'h' }) => {
  const p = PHOTOS[id];
  const set = (ext) => p.widths.map((w) => `/photos/${id}-${w}.${ext} ${w}w`).join(', ');
  return (
    <picture className={`block ${className}`} data-testid={testId || `promo-${id}`}>
      <source type="image/webp" srcSet={set('webp')} sizes={sizes} />
      <img src={`/photos/${id}-${p.widths[0]}.png`} alt={alt} width={p.w} height={p.h} className={`${fit === 'w' ? 'w-full h-auto' : 'h-full w-auto'} max-w-none object-contain select-none`}
        style={{ aspectRatio: `${p.w} / ${p.h}` }} loading={priority ? 'eager' : 'lazy'} decoding="async" draggable={false} />
    </picture>
  );
};

/* Hero stage. Desktop: 3 columns — side columns hold the people (absolutely placed INSIDE their own column, so they can
   only ever overlap each other or the logo wings), centre column holds the logo and, below it, the reserved text band. */
export const HeroStage = ({ children }) => (
  <div className="relative mx-auto max-w-[1400px]" data-testid="hero-stage">
    {/* Desktop / tablet composition */}
    <div className="hidden md:grid grid-cols-[minmax(150px,1fr)_minmax(0,400px)_minmax(150px,1fr)] lg:grid-cols-[minmax(240px,1fr)_minmax(0,480px)_minmax(240px,1fr)] gap-x-4 lg:gap-x-6">
      <div className="relative" data-testid="hero-left-column">
        <PromoPhoto id="hero-man-black" priority sizes="260px" alt="Man in a black shirt" className="absolute top-[2%] right-[3%] z-10 h-[36%] lg:h-[58%] drop-shadow-[0_20px_30px_rgba(136,19,55,0.35)]" />
        <PromoPhoto id="hero-girl-redbag" priority sizes="280px" alt="Woman holding a red handbag" className="absolute hidden lg:block bottom-0 left-[6%] z-20 h-[40%] xl:h-[48%] drop-shadow-[0_20px_30px_rgba(136,19,55,0.35)]" />
      </div>
      <div className="relative flex flex-col items-center" data-testid="hero-center-column">
        <div className="relative w-[120%] max-w-none -mx-[10%] flex justify-center">
          <BrandLogo variant="wings" className="w-full h-auto drop-shadow-[0_24px_50px_rgba(190,24,93,0.4)]" sizes="(max-width: 1024px) 480px, 580px" priority testId="hero-brand-logo" />
        </div>
      </div>
      <div className="relative" data-testid="hero-right-column">
        <PromoPhoto id="hero-girl-lace" priority sizes="240px" alt="Woman in a white lace dress" className="absolute top-[2%] left-[3%] z-10 h-[36%] lg:h-[54%] drop-shadow-[0_20px_30px_rgba(136,19,55,0.35)]" />
        <PromoPhoto id="hero-man-denim" priority sizes="280px" alt="Man in a denim shirt" className="absolute hidden lg:block bottom-0 right-[6%] z-20 h-[48%] xl:h-[56%] drop-shadow-[0_20px_30px_rgba(136,19,55,0.35)]" />
      </div>
    </div>
    {/* Mobile composition: logo → two people side by side → text */}
    <div className="md:hidden flex flex-col items-center" data-testid="hero-mobile">
      <BrandLogo variant="wings" className="w-[min(72vw,280px)] h-auto drop-shadow-[0_16px_30px_rgba(190,24,93,0.35)]" sizes="280px" priority testId="hero-brand-logo-mobile" />
      <div className="mt-4 grid grid-cols-2 gap-3 w-full max-w-[340px]" data-testid="hero-mobile-people">
        <PromoPhoto id="hero-man-black" priority sizes="120px" alt="Man in a black shirt" className="h-[230px] flex justify-end" />
        <PromoPhoto id="hero-girl-lace" priority sizes="120px" alt="Woman in a white lace dress" className="h-[230px] flex justify-start" />
      </div>
    </div>
    <div className="relative z-30 mt-6 md:-mt-4 w-full text-center" data-testid="hero-text-safe-zone">{children}</div>
  </div>
);

/* Section photo panel: a rounded gradient card with 1–3 cut-outs, each in its own flex cell (no overlap). */
export const PhotoPanel = ({ ids, tone = 'rose', className = '', heights = 'h-[260px] sm:h-[320px]', testId = 'photo-panel' }) => {
  const bg = { rose: 'from-rose-100 via-pink-100 to-amber-50', indigo: 'from-indigo-100 via-blue-50 to-rose-50', ruby: 'from-rose-900/40 via-pink-800/30 to-amber-700/30' }[tone];
  return (
    <div className={`rounded-3xl bg-gradient-to-br ${bg} px-4 pt-6 overflow-hidden flex items-end justify-center gap-4 sm:gap-8 ${heights} ${className}`} data-testid={testId}>
      {ids.map((id) => <PromoPhoto key={id} id={id} className="h-full" sizes="(max-width: 640px) 160px, 260px" />)}
    </div>
  );
};

/* Avatar for a NAMED person — resolves strictly through content/namedPeople.js so a name can never get an unrelated photo. */
export const PersonAvatar = ({ personId, className = 'w-12 h-12' }) => {
  const p = personAsset(personId);
  return (
    <picture data-testid={`person-avatar-${personId}`} data-person-name={p.name} className={`block shrink-0 rounded-full ${className}`}>
      <source type="image/webp" srcSet={p.asset} />
      <img src={p.fallback} alt={`${p.name}, FlameLink promotional model`} width={256} height={256} className="w-full h-full rounded-full object-cover" loading="lazy" decoding="async" />
    </picture>
  );
};

/* Login/registration side panel (desktop only). Two cut-outs in their own flex cells; the form lives in a separate grid column. */
export const AuthPhotoPanel = () => (
  <aside className="hidden lg:flex flex-col justify-between rounded-3xl bg-gradient-to-br from-rose-500 via-pink-500 to-amber-400 text-white p-8 overflow-hidden shadow-xl" data-testid="auth-photo-panel">
    <div className="relative z-10 max-w-sm">
      <p className="text-sm uppercase tracking-[0.2em] text-white/80 mb-2">Welcome to FlameLink</p>
      <h2 className="text-2xl font-bold leading-snug">Real people. Real cities. Meaningful connections.</h2>
      <p className="mt-3 text-white/85 text-sm">Verified profiles, AI-powered compatibility and a community that takes dating seriously.</p>
    </div>
    <div className="mt-8 -mx-8 -mb-8 flex items-end justify-center gap-3" data-testid="auth-photo-cells">
      <PromoPhoto id="men21" fit="w" className="w-[42%]" sizes="260px" alt="Smiling man, FlameLink promotional photo" />
      <PromoPhoto id="group8" fit="w" className="w-[50%]" sizes="300px" alt="Group of friends, FlameLink promotional photo" />
    </div>
  </aside>
);
